import {
  AfterViewInit, ChangeDetectorRef,
  Component, DoCheck,
  ElementRef,
  EventEmitter,
  Host, HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output, SkipSelf,
  ViewChild
} from '@angular/core';
import {AbstractControl, ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {coerceBooleanProperty, coerceNumberProperty} from '@angular/cdk/coercion';
import {Subscription} from 'rxjs/Subscription';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {FormSelectItem} from '@common/form-components';
import {FromEventObservable} from 'rxjs/observable/FromEventObservable';
import {fromEvent} from 'rxjs/observable/fromEvent';
import {Subject} from 'rxjs/Subject';

@Component({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FormSelectSingleIsComponent,
    }
  ],
  selector: 'app-form-select-single-is',
  templateUrl: './form-select-single-is.component.html',
  styleUrls: ['./form-select-single-is.component.scss']
})
export class FormSelectSingleIsComponent implements AfterViewInit, OnDestroy, ControlValueAccessor, DoCheck {

  // Label del Input
  @Input() label = '';

  @Input() labelSize: 'sm' | 'md' = 'sm';

  // Mantener el espacio del label, desactivar para casos donde no se requiere label
  _keepLabelSpace = true;
  @Input() set keepLabelSpace(val: boolean) {
    this._keepLabelSpace = coerceBooleanProperty(val);
  }

  // Placeholder del Select
  @Input() placeholder = '';

  // Mensaje de error Inferior (solo acepta uno, podria prepararse una property para un array y mostrar en vertical)
  @Input() errorMessage = '';

  // Mantener el espacio del error, desactivar si no hay validaciones (mantener en formularios para evitar conflictos de tamaño de inputs)
  _keepErrorSpace = true;
  @Input() set keepErrorSpace(val: boolean) {
    this._keepErrorSpace = coerceBooleanProperty(val);
  }

  // Icono de google (fonts.google.com/icons)
  @Input() googleIconName = '';

  // Icono SVG local
  @Input() svgIconName = '';

  // Icono SVG local
  @Input() noOptionsLabel = '';

  _menuOptions: FormSelectItem[] = [];
  @Input() set menuOptions(val: FormSelectItem[]) {
    this._menuOptions = [...val];
    this._allOptions = [...val];
  }

  // usamos esta variable como backup
  _allOptions: FormSelectItem[] = [];

  // Mostrar el boton X para eliminar
  _allowClearButton = false;
  @Input() set allowClearButton(val: boolean) {
    this._allowClearButton = coerceBooleanProperty(val);
  }

  // Mostrar un spinner en lugar del icono (se recomienda deshabilitar el componente)
  _loading = false;
  @Input() set loading(val: boolean) {
    this._loading = coerceBooleanProperty(val);
  }

  // Flag menu abierto
  overlayIsOpen = false;

  // offset Y
  offsetY = 5;

  @ViewChild('controlContainer')
  private controlContainerElement: ElementRef;

  // Evento de inicializacion en caso de usar NGIF
  @Output() initialized = new EventEmitter();
  // Subscripcion para la searchbar, setear la variable para que se desuscriba al eliminar el componente
  public searchSubscription = new Subscription();

  // tooltip en hover
  @Input() tooltip = '';
  // posiicon del tooltip
  @Input() tooltipPosition: 'left' | 'right' | 'above' | 'below' | 'before' | 'after' = 'above';
  // clase generica del tooltip (para añadir en styles.scss)
  @Input() tooltipClass = '';

  // Atributos de Form

  // Valor del Selector
  value: FormSelectItem | null = null;

  // Flag Touched
  touched = false;

  // Flag Disabled
  disabled = false;

  // Flag Control Valido
  validStatus = false;

  // Hacky, levantamos el control usando el mismo nombre de property que se usa para acceder al form
  @Input() formControlName: string;
  // Control del Form
  private control: AbstractControl;
  // Subscripcion al estado Valid
  private statusSubscription: Subscription = new Subscription();

  // valor de la barra de busqueda
  searchValue = '';
  searchValueSubject = new BehaviorSubject<string>('');
  $searchValue = this.searchValueSubject.asObservable();

  inputValue = '';
  optionSelectedRequired = false;

  _integratedSearch = true;
  // Definimos si realizamos una busqueda dentro del componente usando un criterio de comparacion de string con el Label
  // en caso de requerir una busqueda mas avanzada se debe realizar de forma externa
  @Input() set integratedSearch(val: boolean) {
    this._integratedSearch = coerceBooleanProperty(val);
  }

  _searchLoading = false;
  // Spinner de carga en la barra de busqueda. Util en busquedas externas
  @Input() set searchLoading(val: boolean) {
    this._searchLoading = coerceBooleanProperty(val);
  }

  // Callback al cambiar el valor del input
  onChange = (val) => {};

  // Callback al cambiar a estado Touched
  onTouched = () => {};

  private hostClicked = false;

  private destroyed = false;

  // En el constructor obtenemos el formGroup al cual pertenece el input
  destroySubject = new Subject<void>();
  constructor(
    @Optional() @Host() @SkipSelf()
    private controlContainer: ControlContainer,
    private detectionRef: ChangeDetectorRef,
  ) {
    // dado que al hacer un resize en la ventana no se ejecuta el detection changes, lo ejecutamos manualmente
    fromEvent(window, 'resize').takeUntil(this.destroySubject).subscribe(x => {
      this.forceDetectChanges();
    })
  }

  ngDoCheck() {
    if (this.control && this.control.touched && !this.touched) {
      this.markAsTouched();
    }
  }

  @HostListener('click')
  onClickEvent() {
    this.hostClicked = true;
  }

  @HostListener('document:click')
  onClickAllEvent() {
    setTimeout(() => {
      if (!this.hostClicked && this.overlayIsOpen) {
        this.closeMenu();
      }
      this.hostClicked = false;
    })
  }

  ngAfterViewInit() {
    this.initializeFormControl();
    window.setTimeout(() => {
      this.initialized.emit();
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.statusSubscription.unsubscribe();
    this.searchSubscription.unsubscribe();
    this.destroySubject.next();
  }

  // Ejecutamos una busqueda interna
  executeIntegratedSearch() {
    // si encontramos un valor de busqueda, realizamos la busqueda pertinente
    if (this.inputValue.trim().length) {
      this._menuOptions = this._allOptions.filter(x => x.Label.toLowerCase().trim().includes(this.inputValue.toLowerCase().trim()))
    } else {
      this._menuOptions = [...this._allOptions];
    }
  }

  forceDetectChanges() {
    // en caso de que el ciclo de detection changes no se ejecute, podemos llamar a esta funcion
    this.detectionRef.detectChanges();
  }

  initializeFormControl() {
    if (!this.controlContainer) {
      console.warn('Can\'t find parent FormGroup directive');
      return;
    }

    if (!this.formControlName) {
      console.warn('Missing FormControlName directive from host element of the component');
      return;
    }

    this.control = this.controlContainer.control.get(this.formControlName);
    this.validStatus = this.control.valid;
    this.statusSubscription = this.control.statusChanges.subscribe(x => {
      this.validStatus = !this.control.invalid; // usamos invalid pues valid posee un bug
    });
  }

  openMenu() {
    if (!this.disabled) {
      this.overlayIsOpen = true;
    }
  }

  closeMenu() {
    this.overlayIsOpen = false;
    if (this.optionSelectedRequired) {
      this.optionSelectedRequired = false;
      this.inputValue = this.value ? this.value.Label : '';
      this.onSearchChanged();
    }
    this.markAsTouched();
  }

  // cuando hay un cambio en el select, llamamos a la callbackFunction onchange para que lo registre el form
  selectItem(item: FormSelectItem) {
    if (this.value !== item) {
      this.value = item;
      this.onChange(this.value);
      this.optionSelectedRequired = false;
    }
    if (this.value.Label !== this.inputValue) {
      this.inputValue = item.Label;
      this.onSearchChanged();
    }
    this.closeMenu();
  }

  // Eliminar la seleccion
  clearValue() {
    if (this.disabled) {
      return;
    }
    this.value = null;
    this.inputValue = '';
    this.onSearchChanged();
    this.onChange(this.value);
  }

  onSearchChanged() {
    // validamos si tenemos un search interno y seleccionamos solo uno para realizar el search solo una vez
    if (this._integratedSearch) {
      this.executeIntegratedSearch();
    } else {
      this.searchValueSubject.next(this.inputValue);
    }
    this.optionSelectedRequired = true;
    // disparamos el evento por si el sistema de overlay no acomoda el panel y necesita mas espacio
    window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 10);
  }

  /** En caso de que se quiera modificar el valor por codigo
   * mediante AbstractControl.setValue(value) se llamará a este método
   * */
  writeValue(val: FormSelectItem) {
    this.value = val;
    this.inputValue = this.value ? this.value.Label : '';
    this.onSearchChanged();
  }

  /** El formulario registrara esta callbackFunction para notificar cuando hubo un
   * cambio en el formulario del lado del usuario
   * */
  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  /** El formulario registrara esta callbackFunction para notificar el estado Touched
   * */
  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  /** El formulario podra usar esta funcion para deshabilitar el control
   * */
  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  // Marcamos el control como Touched
  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/common/form-components/form-select-single-is/form-select-single-is.component.ts